import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { replace } from 'connected-react-router';
import moment from 'moment-timezone';

import Strings from '../../../Strings';
import Table3 from '../../../containers/Table/Table3';
import { DeviceInvoicesTypes, extractErrorMessage, extractExceptionMessage, getPricingColumns, isDeviceBasedPricingType, PricingTabsDefinition } from './helpers';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { CREATE_DEVICE_INVOICE_PRICING_RESULT, DELETE_DEVICE_INVOICE_PRICING_RESULT, GET_DEVICE_INVOICE_PRICING_FORM_BUNDLE_RESULT, GET_DEVICE_INVOICE_PRICING_ROWS_RESULT, invoiceActions, UPDATE_DEVICE_INVOICE_PRICING_RESULT } from './redux/actions';
import { Tab, Tabs } from '../../../components/Tabs';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { DateFilters } from '../../../utils/DateFilters';
import { dateFilter, getFiltersDefinition, ManufactuterFilter, OrgFilter } from '../../../components/AdvancedFilters/FiltersDefinition';
import { EditPriceModal, PriceHistoryModal } from './modals';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { openModalAction } from '../../../actions/modal';
import { TextInput } from '../../../components/PageHeader/TextInput';

function Pricing(props) {
  const { showNotification, filters, openConfirmModal } = props;
  const { getDeviceInvoicePricingRows, getDeviceInvoicePricingFormBundle, replacePath } = props;
  const { updateDeviceInvoicePricing, createDeviceInvoicePricing, deleteDeviceInvoicePricing } = props;

  const urlParams = new URLSearchParams(window.location.search);
  const typeFromUrl = urlParams.get('type');

  const [pricingType, setPricingType] = React.useState(
    PricingTabsDefinition.some(tab => tab.key === typeFromUrl) ? typeFromUrl : DeviceInvoicesTypes.INITIAL,
  );
  const [currentPrices, setCurrentPrices] = React.useState([]);
  const [filteredPrices, setFilteredPrices] = React.useState(() => PricingTabsDefinition.reduce((acc, tab) => {
    acc[tab.key] = [];
    return acc;
  }, {}));
  const [bundle, setBundle] = React.useState({ organizations: [], device_models: [], programs: [] });
  const [isLoading, setIsLoading] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showHistoryModal, setShowHistoryModal] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');

  const getData = async () => {
    setIsLoading(true);
    const bundleResp = await getDeviceInvoicePricingFormBundle();
    if (bundleResp.type === GET_DEVICE_INVOICE_PRICING_FORM_BUNDLE_RESULT) {
      setBundle(bundleResp.response || { organizations: [], device_models: [], programs: [] });
    }
    const resp = await getDeviceInvoicePricingRows();
    if (resp.type === GET_DEVICE_INVOICE_PRICING_ROWS_RESULT) {
      const data = [];

      resp.response?.forEach(row => {
        const device = bundleResp?.response?.device_models?.find(dm => dm.id === row.device_model_id);
        const isCurrent = moment(row.effective_from).isSameOrBefore(moment()) && (!row.effective_to || moment(row.effective_to).isSameOrAfter(moment()));
        const isFuture = moment(row.effective_from).isAfter(moment());

        if (isCurrent || isFuture) {
          const historicalPrices = resp.response.filter(
            r => r.device_model_id === row.device_model_id
            && r.pricing_type === row.pricing_type
            && r.organization_id === row.organization_id
            && r.cm_program === row.cm_program
            && r.is_sample === row.is_sample
            && r.id !== row.id
          );
          data.push({
            ...row,
            ...device,
            target: isDeviceBasedPricingType(row.pricing_type) ? row.device_model_label : row.cm_program,
            pricing_scope: !row?.organization_id
              ? Strings.deviceInvoices.defaultPrice
              : row.organization_name
              ? `${Strings.deviceInvoices.organizationPrice}: (${row.organization_name})`
              : Strings.deviceInvoices.organizationPrice,
            isCurrent,
            isFuture,
            isPriceSet: row.price !== null && row.price !== undefined,
            historicalPrices,
          });
        }
      });
      setCurrentPrices(data);
    } else {
      showNotification(extractErrorMessage(resp), 5000, true);
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    if (pricingType) {
      const newUrlParams = new URLSearchParams(window.location.search);
      newUrlParams.set('type', pricingType);
      replacePath(`${window.location.pathname}?${newUrlParams}`);
    }
  }, [pricingType, replacePath]);

  React.useEffect(() => {
    getData();
  }, []);

  React.useEffect(() => {
    if (!currentPrices?.length) {
      setFilteredPrices(PricingTabsDefinition.reduce((acc, tab) => {
        acc[tab.key] = [];
        return acc;
      }, {}));
      return;
    }

    const newFilteredPrices = PricingTabsDefinition.reduce((acc, tab) => {
      let ret = currentPrices?.filter(o => o.pricing_type === tab.key) || [];
      
      if (searchQuery) {
        const propertiesToSearch = ['device_model_label', 'cm_program', 'manufacturer', 'organization_name'];
        ret = ret.filter(s => {
          return propertiesToSearch.some(prop => {
            const value = s[prop]?.toLowerCase() || '';
            return value.includes(searchQuery.toLowerCase());
          });
        });
      }
      
      const appliedFilters = getRelevantFilters(filters);
      const filtersToCheck = [
        { filterGrp: 'manufacturer', key: 'manufacturer' },
        { filterGrp: 'DeviceType', key: 'device_type' },
        { filterGrp: 'organization_id', key: 'organization_id' },
      ];
      if (tab.key === DeviceInvoicesTypes.RECURRING) {
        filtersToCheck.push({ filterGrp: 'programCheckbox', key: 'cm_program' });
      }

      const hasFilters = filtersToCheck.some(f => appliedFilters?.some(af => af.group === f.filterGrp));
      if (hasFilters) {
        filtersToCheck.forEach(filterGroup => {
          const filteredByGroup = appliedFilters?.filter(f => f.group === filterGroup.filterGrp);
          if (filteredByGroup?.length) {
            ret = ret.filter(p => filteredByGroup.some(f => f.value === p[filterGroup.key]));
          }
        });
      }

      acc[tab.key] = ret;
      return acc;
    }, {});
    
    setFilteredPrices(newFilteredPrices);
  }, [currentPrices, filters, searchQuery]);

  const onEdit = (row) => {
    setSelectedRow(row);
    setShowEditModal(true);
  };

  const showHistoricalPrices = (row) => {
    setSelectedRow(row);
    setShowHistoryModal(true);
  };

  const handleEdit = async (updatedData) => {
    if (updatedData.price === '' || updatedData.price === null || updatedData.price === undefined || !updatedData.effective_from) {
      showNotification(Strings.deviceInvoicePricingValidationError, 4000, true);
      return false;
    }

    const payload = {
      pricing_type: updatedData.pricing_type,
      organization_id: updatedData.organization_id || undefined,
      effective_from: updatedData.effective_from ? updatedData.effective_from.format("YYYY-MM-DD") : undefined,
      effective_to: updatedData.effective_to ? updatedData.effective_to.format("YYYY-MM-DD") : undefined,
      price: parseFloat(updatedData.price),
      device_model_id: updatedData.device_model_id ? Number(updatedData.device_model_id) : undefined,
      is_sample: !!updatedData.is_sample,
    };

    if (updatedData.pricing_type === DeviceInvoicesTypes.RECURRING) {
      payload.cm_program = updatedData.cm_program || undefined;
    }

    const shouldUpdateExistingPrice = updatedData.isPriceSet && updatedData.id;
    const action = shouldUpdateExistingPrice
      ? updateDeviceInvoicePricing(updatedData.id, payload)
      : createDeviceInvoicePricing(payload);

    const expectedType = shouldUpdateExistingPrice ? UPDATE_DEVICE_INVOICE_PRICING_RESULT : CREATE_DEVICE_INVOICE_PRICING_RESULT;

    return action.then(resp => {
      if (resp.type === expectedType) {
        showNotification(shouldUpdateExistingPrice ? Strings.deviceInvoicePricingUpdated : Strings.deviceInvoicePricingCreated, 4000, false);
        setSelectedRow(null);
        setShowEditModal(false);
        getData();
        return true;
      }
      showNotification(extractErrorMessage(resp), 5000, true);
      return false;
    }).catch(err => {
      showNotification(extractExceptionMessage(err), 5000, true);
      return false;
    });
  }

  const onDelete = price => {
    const modalData = {
      title: Strings.deviceInvoices.deletePriceWarning,
      onConfirmAction: () => deleteDeviceInvoicePricing(price.id).then(resp => {
        if (resp.type === DELETE_DEVICE_INVOICE_PRICING_RESULT) {
          showNotification(Strings.deviceInvoices.priceDeleted, 4000, false);
        } else {
          showNotification(extractErrorMessage(resp), 5000, true);
        }
      }),
      confirmPostAction: getData,
      caption: Strings.deviceInvoices.deletePrice,
      hideCaution: true,
      onCancelAction: null,
    };
    openConfirmModal(modalData);
  };

  const columns = useMemo(() => {
    return getPricingColumns(pricingType);
  }, [pricingType]);

  const addPriceBasedOnRow = (row) => {
    const { id, price, effective_from, effective_to, isPriceSet, isCurrent, isFuture, historicalPrices, ...rest } = row;
    setSelectedRow({
      ...rest,
      isPriceSet: false,
      effective_from: effective_to ? moment(effective_to).format() : moment().format(),
    });
    setShowEditModal(true);
  }

  const tableButtons = useMemo(() => [
    {
      icon: 'plus',
      text: Strings.deviceInvoices.setNewPrice,
      onClick: (_, row) => addPriceBasedOnRow(row),
      visible: (_, row) => row.isCurrent || row.isFuture,
    },
    {
      icon: 'edit',
      text: Strings.edit,
      onClick: (_, row) => onEdit(row, true),
      visible: (_, row) => row.isCurrent || row.isFuture,
    },
    {
      icon: 'delete',
      text: Strings.delete,
      onClick: (_, row) => onDelete(row),
      visible: (_, row) => row.isFuture,
    },
    {
      icon: 'preview',
      text: Strings.deviceInvoices.viewPricesHistory,
      onClick: (_, row) => showHistoricalPrices(row),
      visible: (_, row) => row.historicalPrices?.length > 0,
    }
  ], []);

  const manufacturers = useMemo(() => {
    const manufacturersSet = new Set(bundle?.device_models?.map(dm => dm.manufacturer));
    return Array.from(manufacturersSet);
  }, [bundle]);

  const getTabRightContent = () => {
    return (
      <>
        <TextInput placeholder={Strings.search} onChange={e => setSearchQuery(e.target.value)} />
        <AdvancedFilters>
          {bundle.organizations?.length > 0 && (
            <Filter
              definition={OrgFilter(bundle.organizations.map(o => ({ ...o, masked_id: o.id })), false, 'organization_id', Strings.customer).organization_id}
            />
          )}
          <Filter definition={getFiltersDefinition({ includeHub: true }).DeviceType} />
          <Filter definition={ManufactuterFilter(manufacturers).manufacturer} />
          {pricingType === DeviceInvoicesTypes.RECURRING && (
            <Filter definition={getFiltersDefinition().programCheckbox} />
          )}
        </AdvancedFilters>
        <Button
          class={HEADER_BUTTON_DARK_BLUE}
          onClick={() => { setSelectedRow({}); setShowEditModal(true); }}
          title={Strings.deviceInvoices.addPrice}
        />
      </>
    );
  };

  return (
    <>
      <Tabs
        defaultIndex={PricingTabsDefinition.findIndex(t => t.key === pricingType)}
        onChange={i => setPricingType(PricingTabsDefinition[i].key)}
        rightContent={getTabRightContent()}
      >
        {PricingTabsDefinition.map(tab => (
          <Tab
            key={tab.key}
            label={tab.label}
            count={filteredPrices[tab.key].length}
          >
            <AdvancedFiltersBar
              customFilters={{
                ...ManufactuterFilter(manufacturers),
                ...dateFilter({ ...DateFilters }),
                ...OrgFilter(bundle.organizations.map(o => ({ ...o, masked_id: o.id })), false, 'organization_id', Strings.customer)
              }}
            />
            <Table3
              isLoading={isLoading}
              name={`device-invoices-pricing-${pricingType}`}
              data={filteredPrices[pricingType]}
              columns={columns}
              buttons={tableButtons}
              enableColumnFiltering
            />
          </Tab>
        ))}
      </Tabs>
      {showEditModal && selectedRow && (
        <EditPriceModal
          price={selectedRow}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleEdit}
          organizations={bundle?.organizations}
          devices={bundle?.device_models}
          programs={bundle?.programs}
        />
      )}
      {showHistoryModal && selectedRow && (
        <PriceHistoryModal
          price={selectedRow}
          onClose={() => {setShowHistoryModal(false); setSelectedRow(null);}}
        />
      )}
    </>
  );
}

Pricing.propTypes = {
  createDeviceInvoicePricing: PropTypes.func.isRequired,
  deleteDeviceInvoicePricing: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getDeviceInvoicePricingFormBundle: PropTypes.func.isRequired,
  getDeviceInvoicePricingRows: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
  replacePath: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  updateDeviceInvoicePricing: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  getDeviceInvoicePricingRows: params => dispatch(invoiceActions.getDeviceInvoicePricingRows(params)),
  getDeviceInvoicePricingFormBundle: () => dispatch(invoiceActions.getDeviceInvoicePricingFormBundle()),
  createDeviceInvoicePricing: data => dispatch(invoiceActions.createDeviceInvoicePricing(data)),
  updateDeviceInvoicePricing: (id, data) => dispatch(invoiceActions.updateDeviceInvoicePricing(id, data)),
  deleteDeviceInvoicePricing: id => dispatch(invoiceActions.deleteDeviceInvoicePricing(id)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
  replacePath: path => dispatch(replace(path)),
});


export default connect(mapStateToProps, mapDispatchToProps)(Pricing);
